import React, { useEffect, useState } from 'react'
import { Btn } from '../../AbstractElements'
import { Input, InputGroup, Col, Label, Alert, Spinner, Table, Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap'
import { inviaImportazione, Azione } from '../../hook/Importazione/api'
import { toast } from 'react-toastify'
import { get } from '../../hook/request'
import DeleteConfirm from '../../Components/Modals/Delete'

type TipoImportazione = 'clienti' | 'anagrafica' | 'modelli' | 'prodotti_vendite' | 'bolle' | 'taglie'

type ImportazioneProps = {
  tipo: TipoImportazione
}

type ListaItem = {
  ID: number
  nome: string
  data_ins: string
  codice?: string | null
  negozio?: string | null
  ext?: string | null
}

type ListaResponse = {
  status: 'ok' | 'error'
  list: ListaItem[]
}

type AiutoImportazione = {
  ID: number
  nome_it: string
  descrizione_it: string
}

function fmtDateTime(it: string) {
  if (!it) return '-'
  const s = it.replace(' ', 'T')
  const d = new Date(s)
  if (isNaN(d.getTime())) return it
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const yyyy = d.getFullYear()
  const hh = String(d.getHours()).padStart(2, '0')
  const min = String(d.getMinutes()).padStart(2, '0')
  return `${dd}/${mm}/${yyyy} ${hh}:${min}`
}

function unwrapPayload(res: any) {
  return res?.data?.data ?? res?.data ?? res
}

const SIZE_COLUMN_OPTIONS = Array.from({ length: 8 }, (_, index) => `SIZE ${index + 2}`)
const BOLLA_FORMAT_ERROR = 'Formato file bolla non valido'

const CONFIG: Record<
  TipoImportazione,
  {
    title: string
    templateFile: string
    listSuffix: string
    requiresNegozio: boolean
    showNegozioColumn: boolean
    descFile: string
    codiceAiuto: string
  }
> = {
  clienti: {
    title: 'Anagrafe Clienti',
    codiceAiuto: 'Anagrafica',
    templateFile: 'Anagrafe.csv',
    listSuffix: 'clients',
    requiresNegozio: false,
    showNegozioColumn: false,
    descFile: 'I file .csv devono essere separati dal punto e virgola ( ; )',
  },
  anagrafica: {
    title: 'Clienteling',
    codiceAiuto: 'Clienteling',
    templateFile: 'Clienteling.csv',
    listSuffix: 'rdv',
    requiresNegozio: true,
    showNegozioColumn: true,
    descFile: 'I file .csv devono essere separati dal punto e virgola ( ; )',
  },
  modelli: {
    title: 'Modelli',
    codiceAiuto: 'Modelli',
    templateFile: 'Modelli.csv',
    listSuffix: 'modelli',
    requiresNegozio: false,
    showNegozioColumn: false,
    descFile: 'I file .csv devono essere separati dal punto e virgola ( ; )',
  },
  taglie: {
    title: 'Taglie',
    codiceAiuto: 'Taglie',
    templateFile: 'Taglie.csv',
    listSuffix: 'taglie',
    requiresNegozio: false,
    showNegozioColumn: false,
    descFile: 'Possono essere importati file formato .xls o .xlsx',
  },
  prodotti_vendite: {
    title: 'Prodotti / Vendite',
    codiceAiuto: 'Articoli Venduti',
    templateFile: 'ProdottiVendite.csv',
    listSuffix: 'prodotti_vendite',
    requiresNegozio: false,
    showNegozioColumn: false,
    descFile: 'I file .csv devono essere separati dal punto e virgola ( ; )',
  },
  bolle: {
    title: 'Bolle',
    codiceAiuto: 'Bolle',
    templateFile: 'bolle.csv',
    listSuffix: 'bolla',
    requiresNegozio: true,       
    showNegozioColumn: true,
    descFile: 'Possono essere importati file csv (con separtore il ";" ) oppure file con formato .xls o .xlsx',
  },
}

export default function Importazione({ tipo }: ImportazioneProps) {
  const cfg = CONFIG[tipo]

  const [selezione, setSelezione] = useState<Azione>('aggiungi')
  const [file, setFile] = useState<File | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const [lista, setLista] = useState<ListaItem[]>([])
  const [loadingLista, setLoadingLista] = useState(false)
  const [erroreLista, setErroreLista] = useState<string | null>(null)

  const [negozio, setNegozio] = useState<string>('')
  const [aiutoOpen, setAiutoOpen] = useState(false)
  const [aiutoLoading, setAiutoLoading] = useState(false)
  const [aiutoError, setAiutoError] = useState<string | null>(null)
  const [aiuto, setAiuto] = useState<AiutoImportazione | null>(null)
  const [showColumnSelect, setShowColumnSelect] = useState(false)
  const [column, setColumn] = useState('')

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null)
    setSuccess(null)
    setShowColumnSelect(false)
    setColumn('')
    const f = e.target.files?.[0] ?? null
    setFile(f)
  }

  const loadLista = async () => {
    setErroreLista(null)
    setLoadingLista(true)
    try {
      const url = `/rdv/${cfg.listSuffix}`
      const res = await get<any>(url)
      const payload: ListaResponse | any = res?.data ?? res
      const list: ListaItem[] = Array.isArray(payload?.list) ? payload.list : []
      setLista(list)
    } catch {
      setErroreLista('Impossibile caricare la lista.')
    } finally {
      setLoadingLista(false)
    }
  }

  useEffect(() => {
    loadLista()
    if (!cfg.requiresNegozio) setNegozio('')
    setShowColumnSelect(false)
    setColumn('')
  }, [tipo]) 

  const normalizeAiuto = (payload: any): AiutoImportazione | null => {
    const data = payload?.data ?? payload?.item ?? payload?.result ?? payload
    const item = Array.isArray(data) ? data[0] : data

    if (!item) return null

    let meta: any = {}
    try {
      meta = typeof item.meta === 'string' ? JSON.parse(item.meta) : item.meta ?? {}
    } catch {
      meta = {}
    }

    return {
      ID: Number(item.ID ?? item.id ?? 0),
      nome_it: String(item.nome_it ?? meta?.it?.nome ?? item.nome ?? cfg.title),
      descrizione_it: String(item.descrizione_it ?? meta?.it?.descrizione ?? item.descrizione ?? ''),
    }
  }

  const handleOpenAiuto = async () => {
    setAiutoOpen(true)
    setAiutoLoading(true)
    setAiutoError(null)
    setAiuto(null)

    try {
      const res = await get<any>('/data/getByCode', { code: cfg.codiceAiuto })
      const payload = unwrapPayload(res)
      const item = normalizeAiuto(payload)

      if (!item?.descrizione_it) {
        setAiutoError('Nessuna descrizione disponibile.')
        return
      }

      setAiuto(item)
    } catch (err: any) {
      const payload = unwrapPayload(err?.response)
      const message =
        String(payload?.message ?? payload?.msg ?? '').trim() ||
        'Impossibile caricare i passi per l importazione.'

      setAiutoError(message)
      toast.error(message)
    } finally {
      setAiutoLoading(false)
    }
  }

  const closeAiuto = () => {
    if (aiutoLoading) return
    setAiutoOpen(false)
  }

  const isBollaFormatError = (message: string) =>
    tipo === 'bolle' && message.toLowerCase().includes(BOLLA_FORMAT_ERROR.toLowerCase())

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccess(null)

    if (!file) {
      setError('Seleziona un file.')
      return
    }
    if (cfg.requiresNegozio && !negozio.trim()) {
      setError('Inserisci il codice negozio.')
      return
    }
    if (showColumnSelect && !column) {
      setError('Seleziona la colonna corrispondente al SIZE 1.')
      return
    }

    try {
      setLoading(true)

      const res = await inviaImportazione({
        file,
        azione: selezione,
        tipo,
        negozio,
        column: showColumnSelect ? column : undefined,
      })

      const payload = unwrapPayload(res)
      const status = String(payload?.status ?? '').toLowerCase()
      const message =
        String(payload?.message ?? payload?.msg ?? '').trim() ||
        (status === 'ok' ? 'Importazione avvenuta con successo' : 'Operazione non riuscita')

      if (status === 'ok') {
        setSuccess(message)
        setShowColumnSelect(false)
        setColumn('')
        toast.success(message)
        await loadLista()
      } else {
        setError(message)
        if (isBollaFormatError(message)) setShowColumnSelect(true)
        toast.error(message)
      }
    } catch (err: any) {
      const payload = unwrapPayload(err?.response)
      const message =
        String(payload?.message ?? '').trim() ||
        err?.message ||
        'Qualcosa è andato storto'

      setError(message)
      if (isBollaFormatError(message)) setShowColumnSelect(true)
      toast.error(message)
    } finally {
      setLoading(false)
    }
  }


  return (
    <div className="page-body">
      <div className="col-sm-12 pt-4">
        <div style={{ minHeight: '80vh' }} className="card-no-border card-header l-importazione">
          <div className="d-flex justify-content-between">
            <div>
              <h2>Importazione tipo: {cfg.title}</h2>
        
              <small>{cfg.descFile}</small>
            </div>
            <div className="d-flex gap-2 flex-wrap justify-content-end">
              <a
                href={'https://api.customdev.it/projects/meavi/files/example/' + cfg.templateFile}
                className="btn btn-outline-primary btn-sm"
              >
                <i className="fa-solid fa-download me-2"></i>
                Scarica Template
              </a>
              <button
                type="button"
                className="btn btn-outline-info btn-sm"
                onClick={handleOpenAiuto}
                disabled={aiutoLoading}
              >
                {aiutoLoading ? (
                  <Spinner size="sm" className="me-2" />
                ) : (
                  <i className="fa-solid fa-circle-question me-2"></i>
                )}
                Passi per l importazione
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="mt-3">
              <InputGroup>
                {cfg.requiresNegozio && (
                  <Col xs={12} className="mb-3">
                    <Label>Negozio</Label>
                    <Input
                      style={{ maxWidth: 300 }}
                      name="negozio"
                      value={negozio}
                      onChange={e => setNegozio(e.target.value)}
                      placeholder="Codice Negozio"
                      required
                    />
                  </Col>
                )}

                <Btn color="success" id="inputGroupFileAddon03" outline type="button">
                  <i className="fa-solid fa-file" />
                </Btn>
                <Input id="inputGroupFile03" type="file" onChange={handleFileChange} />
              </InputGroup>
            </div>

            {showColumnSelect && (
              <Col xs={12} md={6} lg={4} className="mt-3">
                <Label>Selezionare la colonna corrispondente al SIZE 1</Label>
                <Input
                  type="select"
                  value={column}
                  onChange={e => setColumn(e.target.value)}
                  required
                >
                  <option value="">Scegli...</option>
                  {SIZE_COLUMN_OPTIONS.map(option => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Input>
              </Col>
            )}

            <div className="mt-4 d-flex gap-2">
              <Btn color="primary" type="submit" disabled={loading}>
                {loading ? <Spinner size="sm" /> : 'Invia'}
              </Btn>
            </div>

            <div className="mt-3">
              {error && <Alert color="danger">{error}</Alert>}
              {success && <Alert color="success">{success}</Alert>}
            </div>
          </form>

          <hr className="my-4" />
          <h5 className="mb-3">Importazioni recenti</h5>

          {loadingLista ? (
            <div className="d-flex align-items-center gap-2">
              <Spinner size="sm" /> <span>Caricamento…</span>
            </div>
          ) : erroreLista ? (
            <Alert color="danger">{erroreLista}</Alert>
          ) : lista.length === 0 ? (
            <div className="text-muted">Nessun elemento.</div>
          ) : (
            <div className="table-responsive">
              <Table striped hover className="mb-0">
                <thead>
                  <tr>
                    <th style={{ minWidth: 260 }}>Nome</th>
                    {cfg.showNegozioColumn && <th style={{ minWidth: 160 }}>Negozio</th>}
                    <th style={{ width: 200 }}>Inserito il</th>
                    <th style={{ width: 220 }}>Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {lista.map(item => {
                    const hasCodice = !!item.codice
                    const normalizedExt = item.ext?.toLowerCase().trim()

                    const downloadHref = hasCodice
                      ? normalizedExt === 'xls'
                        ? `https://api.customdev.it/projects/meavi/files/xls/${item.codice}.xls`
                        : `https://api.customdev.it/projects/meavi/files/csv/${item.codice}.csv`
                      : undefined

                    return (
                      <tr key={item.ID}>
                        <td className="align-middle">{item.nome || '-'}</td>

                        {cfg.showNegozioColumn && (
                          <td className="align-middle">{item.negozio || '-'}</td>
                        )}

                        <td className="align-middle">{fmtDateTime(item.data_ins)}</td>

                        <td className="align-middle">
                          <div className="d-flex gap-2">
                            <a
                              className={`btn btn-sm btn-outline-primary ${!hasCodice ? 'disabled' : ''}`}
                              href={downloadHref}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={e => {
                                if (!hasCodice) e.preventDefault()
                              }}
                              title={hasCodice ? 'Scarica CSV' : 'Nessun file disponibile'}
                            >
                              <i className="fa-solid fa-download me-2" />
                              Scarica
                            </a>

                            <DeleteConfirm
                              url={`/import/${item.ID}?user_email=${encodeURIComponent(
                                localStorage.getItem('email') || ''
                              )}`}
                              onSuccess={() => setLista(prev => prev.filter(x => x.ID !== item.ID))}
                              trigger={
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger"
                                  title="Elimina"
                                >
                                  <i className="fa-solid fa-trash me-1" />
                                  Elimina
                                </button>
                              }
                            />
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </div>
      </div>

      <Modal isOpen={aiutoOpen} toggle={closeAiuto} size="lg" centered>
        <ModalHeader toggle={closeAiuto}>
          {aiuto?.nome_it || `Passi per l importazione - ${cfg.title}`}
        </ModalHeader>
        <ModalBody>
          {aiutoLoading ? (
            <div className="d-flex align-items-center gap-2">
              <Spinner size="sm" /> <span>Caricamento...</span>
            </div>
          ) : aiutoError ? (
            <Alert color="danger" className="mb-0">{aiutoError}</Alert>
          ) : (
            <div dangerouslySetInnerHTML={{ __html: aiuto?.descrizione_it ?? '' }} />
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={closeAiuto} disabled={aiutoLoading}>
            Chiudi
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}
